import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, lastValueFrom, map } from 'rxjs';
import { ACCESS_MODE_KEY, AccessMode } from 'src/app/core/authentication/share-token';
import { EnvironmentService } from 'src/app/shared/common/environment/environment.service';
import { Actor } from 'src/app/shared/user/actor';

import { Tag } from '../../common/files/TCFile.service';
import { GroupUsersResponse, UserDTO } from '../../user/user';
import { MapWorkspacePermissionType } from '../mapWorkspace/map-workspace-permission';
import { FolderPermissionsResponse, Permission, permissionPriority, PermissionType } from '../mapWorkspace/permission';
import { Project, ProjectDTO, ProjectSettings, ProjectTaskMigrationStatus } from './project';

@Injectable({
    providedIn: 'root'
})
export class ProjectService {
    constructor(private http: HttpClient, private env: EnvironmentService) {}

    private get projectPath(): string {
        return this.env.connectApiUrl + '/projects';
    }

    private get connectApiUrl(): string {
        return this.env.connectApiUrl;
    }

    public getProjects(): Promise<Project[]> {
        return lastValueFrom(this.http.get<ProjectDTO[]>(this.projectPath)).then(projects =>
            projects.map(project => Project.fromDTO(project))
        );
    }

    public createProject(data: ProjectDTO): Promise<Project> {
        return lastValueFrom(this.http.post<ProjectDTO>(this.projectPath, data)).then(project =>
            Project.fromDTO(project)
        );
    }

    public getFirstOrDefaultProject(): Promise<Project> {
        return lastValueFrom(this.http.get<ProjectDTO[]>(this.projectPath)).then(projects => {
            if (projects.length) {
                return Promise.resolve(Project.fromDTO(projects[0]));
            } else {
                return Promise.reject({
                    data: {
                        errorcode: 'PROJECT_NOT_FOUND',
                        message: 'Please create new project from trimble connect.'
                    }
                });
            }
        });
    }

    public getProjectById(id: string): Promise<Project> {
        if (sessionStorage.getItem(ACCESS_MODE_KEY) && sessionStorage.getItem(ACCESS_MODE_KEY) === AccessMode.SHARED) {
            return Promise.resolve(Project.fromDTO({ id, name: 'Shared Project' }));
        }

        if (!id) {
            return this.getFirstOrDefaultProject();
        } else {
            const path = this.projectPath + '/' + id + '?fullyLoaded=false';

            return lastValueFrom(this.http.get<ProjectDTO>(path)).then(project => Project.fromDTO(project));
        }
    }

    public FindProjectRegionById(regionPath: string, id: string): Promise<Project> {
        if (sessionStorage.getItem(ACCESS_MODE_KEY) && sessionStorage.getItem(ACCESS_MODE_KEY) === AccessMode.SHARED) {
            return Promise.resolve(Project.fromDTO({ id, name: 'Shared Project' }));
        }
        let path = regionPath + 'projects/' + id + '?fullyLoaded=false';
        return lastValueFrom(this.http.get<ProjectDTO>(path)).then(project => Project.fromDTO(project));
    }

    // function to obtain project settings from tc for example:unitsettings etc.
    public getProjectSettings(id: string): Promise<ProjectSettings> {
        const path = this.projectPath + '/' + id + '/settings';
        if (!id) {
            return Promise.reject(); // TODO: GIM 16/5/2019 - check this conversion
        } else {
            return lastValueFrom(this.http.get<ProjectSettings>(path));
        }
    }

    public searchProjectByName(name: string): Promise<Project> {
        const path = this.connectApiUrl + '/search?type=PROJECT&query=' + name;

        return lastValueFrom(this.http.get<{ items: ProjectDTO[] }>(path)).then(project =>
            Project.fromDTO(project.items[0])
        );
    }

    public getProjectGroups(projectId: string, startIndex: number = 0, pageSize: number = 25): Promise<Actor[]> {
        let endIndex: number = null;
        if (pageSize) {
            endIndex = startIndex + (pageSize - 1);
        }

        // ! It's important to type opts to object so typescript compiler finds right http.get overload
        const opts: object = {
            headers: new HttpHeaders({
                Range: 'items=' + startIndex + '-' + (endIndex || '')
            }),
            observe: 'response',
            responseType: 'json'
        };
        const path = this.projectPath + '/' + projectId + '/actors?type=GROUP';

        return lastValueFrom(this.http.get<HttpResponse<object>>(path, opts)).then(response => {
            let responseRange = response.headers
                .get('content-range')
                .replace('items ', '')
                .replace('--1', '-0')
                .replace('/', '-')
                .split('-');

            return (response.body as any[]).map(group => {
                group.totalItems = responseRange[2];
                return Actor.fromDTO(group, PermissionType.GROUP);
            });
        });
    }

    private getPermissionsFromACL(
        acl: Partial<Record<MapWorkspacePermissionType, string[]>>,
        currentUserTiduuid: string
    ): {
        userPermission: MapWorkspacePermissionType;
        groupPermissions: Array<{ permission: MapWorkspacePermissionType; groupId: string }>;
    } {
        let userPermission: MapWorkspacePermissionType = null;
        const groupPermissions: Array<{ permission: MapWorkspacePermissionType; groupId: string }> = [];

        for (const [permType, entries] of Object.entries(acl)) {
            for (const entry of entries) {
                if (entry.startsWith('users:')) {
                    // Eg: "users:f2xxxx8-cxx5-4x3e-b7x9-071dxxxxxbb6, users:xxxxxx-xxxxb-4xx0-8xxb-xx6a"
                    const userId = entry.substring(6); // Remove "users:".
                    if (userId === currentUserTiduuid) {
                        userPermission = permType as MapWorkspacePermissionType;
                    }
                } else if (entry.startsWith('tc-groups:')) {
                    // Eg: "tc-groups:xxxx8a8-xxb5-4xxe-bxx9-071db66xxxx6, tc-groups:*"
                    const groupId = entry.substring(10); // Remove "tc-groups:".
                    groupPermissions.push({
                        permission: permType as MapWorkspacePermissionType,
                        groupId: groupId
                    });
                }
            }
        }

        return { userPermission, groupPermissions };
    }

    public async getUserOrGroupPermissions(dto: FolderPermissionsResponse, currentUserTiduuid: string) {
        let permission = new Permission();

        if (!dto) {
            return permission;
        }

        let effectivePermission: MapWorkspacePermissionType | null = null;

        // Check direct permissions first, before checking inherited.
        if (dto.directPermissions) {
            const { userPermission, groupPermissions } = this.getPermissionsFromACL(
                dto.directPermissions.acl,
                currentUserTiduuid
            );

            if (userPermission) {
                effectivePermission = userPermission;
            } else if (groupPermissions.length > 0) {
                // Sort by permission priority. FULL_ACCESS has highest priority.
                groupPermissions.sort((a, b) => permissionPriority[a.permission] - permissionPriority[b.permission]);

                // Check group membership
                for (const groupPerm of groupPermissions) {
                    // "tc-groups:*"  means all project users.
                    if (groupPerm.groupId === '*') {
                        effectivePermission = groupPerm.permission;
                        break;
                    }

                    // Check if user is a member of this group.
                    const groupUserUuids = await this.getUserGroupMemberUuids(groupPerm.groupId);
                    if (groupUserUuids.includes(currentUserTiduuid)) {
                        effectivePermission = groupPerm.permission;
                        break;
                    }
                }

                if (!effectivePermission) {
                    effectivePermission = dto.directPermissions.defaultAccess;
                }
            } else {
                effectivePermission = dto.directPermissions.defaultAccess;
            }
        }

        // Check inherited permissions if inheritance is enabled.
        if (dto.inheritedPermissions && dto.directPermissions?.inheritance !== false) {
            const { userPermission: inheritedUserPerm, groupPermissions: inheritedGroupPerms } =
                this.getPermissionsFromACL(dto.inheritedPermissions.acl, currentUserTiduuid);

            if (inheritedUserPerm) {
                // Inherited permission takes precedence if it has higher priority over direct permission.
                if (
                    !effectivePermission ||
                    permissionPriority[inheritedUserPerm] < permissionPriority[effectivePermission]
                ) {
                    effectivePermission = inheritedUserPerm;
                }
            } else if (inheritedGroupPerms.length > 0) {
                inheritedGroupPerms.sort((a, b) => permissionPriority[a.permission] - permissionPriority[b.permission]);

                for (const groupPerm of inheritedGroupPerms) {
                    if (groupPerm.groupId === '*') {
                        if (
                            !effectivePermission ||
                            permissionPriority[groupPerm.permission] < permissionPriority[effectivePermission]
                        ) {
                            effectivePermission = groupPerm.permission;
                        }
                        break;
                    }

                    // Check if user is a member of this group.
                    const groupUserUuids = await this.getUserGroupMemberUuids(groupPerm.groupId);
                    if (groupUserUuids.includes(currentUserTiduuid)) {
                        if (
                            !effectivePermission ||
                            permissionPriority[groupPerm.permission] < permissionPriority[effectivePermission]
                        ) {
                            effectivePermission = groupPerm.permission;
                        }
                        break;
                    }
                }

                if (!effectivePermission) {
                    effectivePermission = dto.inheritedPermissions.defaultAccess;
                }
            } else if (!effectivePermission) {
                effectivePermission = dto.inheritedPermissions.defaultAccess;
            }
        }

        permission.effectivePermission = effectivePermission || MapWorkspacePermissionType.NO_ACCESS;
        permission.defaultPermission =
            dto.directPermissions?.defaultAccess ||
            dto.inheritedPermissions?.defaultAccess ||
            permission.effectivePermission;

        return permission;
    }

    private getUserGroupMemberUuids(groupId: string): Promise<string[]> {
        // Returns active and pending users in a group
        const path = this.connectApiUrl + '/groups/' + groupId + '/users';

        return lastValueFrom(this.http.get(path))
            .then((response: GroupUsersResponse[]) => {
                const userUuids: string[] = response.map(item => item.tiduuid);
                return userUuids;
            })
            .catch(() => {
                return [];
            });
    }

    public getProjectUsers(projectId: string, startIndex: number = 0, pageSize: number = 25): Promise<Actor[]> {
        let endIndex: number = null;
        if (pageSize) {
            endIndex = startIndex + (pageSize - 1);
        }

        // ! It's important to type opts to object so typescript compiler finds right http.get overload
        const opts: object = {
            headers: new HttpHeaders({
                Range: 'items=' + startIndex + '-' + (endIndex || '')
            }),
            observe: 'response',
            responseType: 'json'
        };

        const path = this.projectPath + '/' + projectId + '/actors?type=USER';
        return lastValueFrom(this.http.get<HttpResponse<any>>(path, opts)).then(response => {
            let responseRange = response.headers
                .get('content-range')
                .replace('items ', '')
                .replace('--1', '-0')
                .replace('/', '-')
                .split('-');

            return response.body.map((user: Partial<UserDTO>) => {
                const actor = Actor.fromDTO(user, PermissionType.USER);
                actor.totalItems = Number(responseRange[2]);
                return actor;
            });
        });
    }

    //get project tags
    public getProjectTags(projectId: string): Promise<Tag[]> {
        const path = `${this.env.connectApiUrl}/tags`;
        const params = {
            projectId,
            miniRep: true // to get minimal representation of tags
        };
        return lastValueFrom(this.http.get<Tag[]>(path, { params }));
    }

    public getProjectTaskMigrationStatus(projectId: string, enabled = false): Promise<string> {
        // TODO: TaskMigration - remove 'enabled' flag when if Task Migration is ready for release.
        if (!enabled) return Promise.resolve(ProjectTaskMigrationStatus.NOT_FOUND);

        const path = `${this.env.apiUrl}/projects/${projectId}/taskmigration`;
        return lastValueFrom(
            this.http.get<{ status: string }>(path).pipe(
                map(response => response.status),
                catchError(() => Promise.resolve(ProjectTaskMigrationStatus.NOT_FOUND))
            )
        );
    }

    public startProjectTaskMigration(projectId: string): Promise<any> {
        const path = `${this.env.apiUrl}/projects/${projectId}/taskmigration`;

        return lastValueFrom(this.http.post(path, {}));
    }
}
